if (!customElements.get('article-toc')) {
  customElements.define(
    'article-toc',
    class ArticleToc extends HTMLElement {
      connectedCallback() {
        this.content = document.querySelector(this.dataset.contentSelector || '.article__content');
        this.list = this.querySelector('.article-toc__list');
        this.indicator = this.querySelector('.article-toc__indicator');
        this.titleEl = this.querySelector('.article-toc__title');
        this.nav = this.querySelector('.article-toc__nav');
        this.headerEl = document.querySelector('.header-section header[is="sticky-header"]');
        this.ticking = false;
        this.mql = window.matchMedia('(max-width: 1199.98px)');
        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleTitleClick = this.handleTitleClick.bind(this);
        this.handleTitleKeydown = this.handleTitleKeydown.bind(this);
        this.handleMqlChange = this.handleMqlChange.bind(this);

        if (!this.content || !this.list) {
          this.disable();
          return;
        }

        this.levels = (this.dataset.levels || 'h2,h3')
          .split(',')
          .map((level) => level.trim().toLowerCase())
          .filter(Boolean);
        this.minHeadings = parseInt(this.dataset.minHeadings || '2', 10);
        this.scrollOffset = parseInt(this.dataset.scrollOffset || '100', 10);
        this.links = [];
        this.activeLink = null;

        this.build();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        if (this.mql) this.mql.removeEventListener('change', this.handleMqlChange);
      }

      disable() {
        this.hidden = true;
        const article = this.closest('.article');
        if (article) article.classList.remove('article--has-toc');
      }

      build() {
        const headings = Array.from(this.content.querySelectorAll(this.levels.join(',')))
          .filter((heading) => heading.textContent.trim().length);

        if (headings.length < this.minHeadings) {
          this.disable();
          return;
        }

        const usedIds = {};
        const fragment = document.createDocumentFragment();
        this.list.replaceChildren();

        headings.forEach((heading) => {
          const id = this.ensureId(heading, usedIds);
          const level = heading.tagName.toLowerCase();

          const item = document.createElement('li');
          item.className = `article-toc__item article-toc__item--${level}`;

          const link = document.createElement('a');
          link.className = 'article-toc__link';
          link.href = `#${id}`;
          link.textContent = heading.textContent.trim();

          item.appendChild(link);
          fragment.appendChild(item);
          this.links.push({ heading, item, link });
        });

        this.list.appendChild(fragment);
        this.hidden = false;
        this.bindEvents();
        this.setupCollapse();
        this.observe();
      }

      setupCollapse() {
        if (!this.titleEl) return;

        this.titleEl.setAttribute('role', 'button');
        this.titleEl.setAttribute('tabindex', '0');
        if (this.nav && this.nav.id) {
          this.titleEl.setAttribute('aria-controls', this.nav.id);
        }
        this.titleEl.setAttribute('aria-expanded', 'false');

        this.titleEl.addEventListener('click', this.handleTitleClick);
        this.titleEl.addEventListener('keydown', this.handleTitleKeydown);
        this.mql.addEventListener('change', this.handleMqlChange);
      }

      handleTitleClick() {
        // The collapse behaviour only exists below the desktop sidebar breakpoint.
        if (!this.mql.matches) return;
        this.setOpen(!this.classList.contains('is-open'));
      }

      handleTitleKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        if (!this.mql.matches) return;
        event.preventDefault();
        this.setOpen(!this.classList.contains('is-open'));
      }

      handleMqlChange(event) {
        // Leaving mobile: drop any collapsed state so the desktop sidebar is unaffected.
        if (!event.matches) this.setOpen(false);
      }

      setOpen(open) {
        this.classList.toggle('is-open', open);
        if (this.titleEl) this.titleEl.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      ensureId(heading, usedIds) {
        let id = heading.id || this.slugify(heading.textContent) || 'section';

        if (usedIds[id] != null) {
          usedIds[id] += 1;
          id = `${id}-${usedIds[id]}`;
        } else {
          usedIds[id] = 0;
        }

        heading.id = id;
        return id;
      }

      slugify(text) {
        return text
          .toLowerCase()
          .trim()
          .replace(/[^\p{L}\p{N}\s-]/gu, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      bindEvents() {
        this.list.addEventListener('click', (event) => {
          const link = event.target.closest('a');
          if (!link) return;

          const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
          if (!target) return;

          event.preventDefault();
          const top = target.getBoundingClientRect().top + window.scrollY - this.getHeaderHeight() - this.scrollOffset;
          window.scrollTo({ top, behavior: 'smooth' });
          history.replaceState(null, '', link.hash);
          this.setActive(target.id);

          // On mobile the panel overlaps the content it just jumped to — collapse it.
          if (this.mql.matches) this.setOpen(false);
        });
      }

      observe() {
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize);
        this.updateActiveFromScroll();
      }

      getHeaderHeight() {
        if (!this.headerEl) return 0;

        const height = Math.round(this.headerEl.offsetHeight);
        if (this.headerEl.isAlwaysSticky) return height;

        const isSticky = this.headerEl.classes && document.body.classList.contains(this.headerEl.classes.pinned);
        return isSticky ? height : 0;
      }

      getScrollTargetTop(heading) {
        return heading.getBoundingClientRect().top + window.scrollY;
      }

      handleScroll() {
        if (this.ticking) return;

        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.updateActiveFromScroll();
          this.ticking = false;
        });
      }

      handleResize() {
        this.moveIndicator(this.activeLink && this.activeLink.item);
        this.updateActiveFromScroll();
      }

      updateActiveFromScroll() {
        const currentTop = window.scrollY + this.getHeaderHeight() + this.scrollOffset + 1;
        let active = this.links[0];

        for (const linkData of this.links) {
          if (this.getScrollTargetTop(linkData.heading) <= currentTop) {
            active = linkData;
          } else {
            break;
          }
        }

        if (active) this.setActive(active.heading.id);
      }

      setActive(id) {
        const active = this.links.find(({ link }) => decodeURIComponent(link.hash.slice(1)) === id);
        if (!active || active === this.activeLink) return;

        this.links.forEach(({ item }) => item.classList.remove('is-active'));
        active.item.classList.add('is-active');
        this.activeLink = active;
        this.moveIndicator(active.item);
      }

      moveIndicator(item) {
        if (!this.indicator || !item) return;
        this.indicator.style.transform = `translateY(${item.offsetTop}px)`;
        this.indicator.style.height = `${item.offsetHeight}px`;
      }
    }
  );
}
